import React, { useState } from 'react';

interface ChainMaintenancePanelProps {
  station: any;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '16px',
    color: 'var(--em3-color-text-primary)',
  },
  title: {
    margin: '0 0 16px',
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--em3-color-text-primary)',
  },
  section: {
    marginBottom: '16px',
  },
  button: {
    backgroundColor: 'var(--em3-button-secondary-bg)',
    border: '1px solid var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-default)',
    color: 'var(--em3-button-secondary-text)',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    padding: '8px 14px',
    marginBottom: '8px',
    display: 'inline-block',
  },
  buttonCaution: {
    backgroundColor: 'var(--em3-color-danger-bg, rgba(200, 50, 50, 0.12))',
    border: '1px solid var(--em3-color-danger-border, rgba(200, 50, 50, 0.4))',
    borderRadius: 'var(--em3-border-radius-default)',
    color: 'var(--em3-color-danger)',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    padding: '8px 14px',
    marginBottom: '8px',
    display: 'inline-block',
  },
  buttonDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  resultBox: {
    backgroundColor: 'var(--em3-color-background-main)',
    border: '1px solid var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-default)',
    padding: '10px',
    fontSize: '12px',
    color: 'var(--em3-color-text-secondary)',
    whiteSpace: 'pre-wrap' as const,
    maxHeight: '200px',
    overflowY: 'auto' as const,
    fontFamily: 'monospace',
    marginTop: '6px',
  },
  errorBox: {
    backgroundColor: 'var(--em3-color-danger-bg, rgba(200, 50, 50, 0.12))',
    border: '1px solid var(--em3-color-danger-border, rgba(200, 50, 50, 0.4))',
    borderRadius: 'var(--em3-border-radius-default)',
    padding: '8px 10px',
    fontSize: '12px',
    color: 'var(--em3-color-danger)',
    marginTop: '6px',
  },
  confirmBox: {
    backgroundColor: 'var(--em3-color-danger-bg, rgba(200, 50, 50, 0.08))',
    border: '1px solid var(--em3-color-danger-border, rgba(200, 50, 50, 0.3))',
    borderRadius: 'var(--em3-border-radius-default)',
    padding: '10px',
    fontSize: '13px',
    color: 'var(--em3-color-text-primary)',
    marginTop: '6px',
  },
  confirmButtons: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
  },
  confirmYes: {
    backgroundColor: 'var(--em3-color-danger)',
    border: 'none',
    borderRadius: 'var(--em3-border-radius-default)',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    padding: '5px 12px',
  },
  confirmNo: {
    backgroundColor: 'var(--em3-button-secondary-bg)',
    border: '1px solid var(--em3-color-border)',
    borderRadius: 'var(--em3-border-radius-default)',
    color: 'var(--em3-button-secondary-text)',
    cursor: 'pointer',
    fontSize: '12px',
    padding: '5px 12px',
  },
  spinnerText: {
    fontSize: '12px',
    color: 'var(--em3-color-text-secondary)',
    marginTop: '4px',
  },
};

function formatResult(data: any): string {
  if (!data) return '';
  return JSON.stringify(data, null, 2);
}

function isNetworkError(e: unknown): boolean {
  if (e instanceof TypeError && (e.message === 'Failed to fetch' || e.message.includes('NetworkError'))) {
    return true;
  }
  return false;
}

const ChainMaintenancePanel: React.FC<ChainMaintenancePanelProps> = ({ station }) => {
  const [analyzeResult, setAnalyzeResult] = useState<any | null>(null);
  const [fixResult, setFixResult] = useState<any | null>(null);
  const [recalcResult, setRecalcResult] = useState<any | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRecalcConfirm, setShowRecalcConfirm] = useState<boolean>(false);

  const clearSharedState = () => {
    setError(null);
  };

  const postToApi = async (endpoint: string, body: object): Promise<any> => {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      if (response.status === 502) {
        throw new Error('wx-api unavailable (502 Bad Gateway)');
      }
      const text = await response.text();
      throw new Error(`Request failed (${response.status}): ${text}`);
    }

    return response.json();
  };

  const handleAnalyze = async () => {
    clearSharedState();
    setLoading('analyze');
    setAnalyzeResult(null);
    try {
      const data = await postToApi(`/api/weather-stations/${station.id}/cffdrs/analyze`, {});
      setAnalyzeResult(data);
    } catch (e: unknown) {
      setError(isNetworkError(e) ? 'wx-api unavailable' : (e instanceof Error ? e.message : 'Unknown error'));
    } finally {
      setLoading(null);
    }
  };

  const handleFix = async () => {
    clearSharedState();
    setLoading('fix');
    setFixResult(null);
    try {
      const data = await postToApi(`/api/weather-stations/${station.id}/cffdrs/fix-3-days`, {});
      setFixResult(data);
    } catch (e: unknown) {
      setError(isNetworkError(e) ? 'wx-api unavailable' : (e instanceof Error ? e.message : 'Unknown error'));
    } finally {
      setLoading(null);
    }
  };

  const handleRecalcConfirm = async () => {
    setShowRecalcConfirm(false);
    clearSharedState();
    setLoading('recalc');
    setRecalcResult(null);
    try {
      const data = await postToApi(`/api/weather-stations/${station.id}/cffdrs/recalculate`, {});
      setRecalcResult(data);
    } catch (e: unknown) {
      setError(isNetworkError(e) ? 'wx-api unavailable' : (e instanceof Error ? e.message : 'Unknown error'));
    } finally {
      setLoading(null);
    }
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Chain Maintenance Tools</h3>

      {/* Shared error display */}
      {error && !loading && (
        <div style={styles.errorBox}>{error}</div>
      )}

      {/* Section 1: Analyze */}
      <div style={styles.section}>
        <button
          style={loading === 'analyze' ? { ...styles.button, ...styles.buttonDisabled } : styles.button}
          onClick={handleAnalyze}
          disabled={loading !== null}
        >
          {loading === 'analyze' ? 'Analyzing...' : 'Analyze Chain'}
        </button>
        {loading === 'analyze' && (
          <div style={styles.spinnerText}>Running analysis for {station?.nesdis}...</div>
        )}
        {analyzeResult !== null && loading !== 'analyze' && (
          <div style={styles.resultBox}>
            {Object.keys(analyzeResult).length === 0 ? 'No gaps found' : formatResult(analyzeResult)}
          </div>
        )}
      </div>

      {/* Section 2: Fix */}
      <div style={styles.section}>
        <button
          style={loading === 'fix' ? { ...styles.button, ...styles.buttonDisabled } : styles.button}
          onClick={handleFix}
          disabled={loading !== null}
        >
          {loading === 'fix' ? 'Fixing...' : 'Fix Chain (3 days)'}
        </button>
        {loading === 'fix' && (
          <div style={styles.spinnerText}>Fixing chain for {station?.nesdis}...</div>
        )}
        {fixResult !== null && loading !== 'fix' && (
          <div style={styles.resultBox}>
            {formatResult(fixResult)}
          </div>
        )}
      </div>

      {/* Section 3: Recalculate */}
      <div style={styles.section}>
        <button
          style={loading === 'recalc' ? { ...styles.buttonCaution, ...styles.buttonDisabled } : styles.buttonCaution}
          onClick={() => setShowRecalcConfirm(true)}
          disabled={loading !== null || showRecalcConfirm}
        >
          {loading === 'recalc' ? 'Recalculating...' : 'Recalculate Chain'}
        </button>

        {showRecalcConfirm && (
          <div style={styles.confirmBox}>
            <div>This will recalculate the entire CFFDRS chain from scratch. Continue?</div>
            <div style={styles.confirmButtons}>
              <button style={styles.confirmYes} onClick={handleRecalcConfirm}>
                Yes, Recalculate
              </button>
              <button style={styles.confirmNo} onClick={() => setShowRecalcConfirm(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading === 'recalc' && (
          <div style={styles.spinnerText}>Recalculating chain for {station?.nesdis}...</div>
        )}
        {recalcResult !== null && loading !== 'recalc' && (
          <div style={styles.resultBox}>
            {formatResult(recalcResult)}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChainMaintenancePanel;
